import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Container, Card, Form, Button, Alert, Spinner, Modal } from 'react-bootstrap';
import { useDispatch, useSelector } from 'react-redux';
import { fetchDevices, updateDevice, deleteDevice, selectDevices, selectDevicesLoading, selectDevicesError } from '../../redux/slices/devices';
import { Device } from '../../types';

export type PadColorKeys = 'red' | 'pink' | 'orange' | 'yellow' | 'green' | 'blue' | 'purple';

interface DeviceFormData {
  name: string;
  serialNumber: string;
  inClinicLocation: string;
  inClinicLocationNumber: string;
  colorCode: PadColorKeys;
  active: boolean;
}

const EditDevicePage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const dispatch = useDispatch<any>();
  const devices = useSelector(selectDevices);
  const loading = useSelector(selectDevicesLoading);
  const error = useSelector(selectDevicesError);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  const [formData, setFormData] = useState<DeviceFormData>({
    name: '',
    serialNumber: '',
    inClinicLocation: '',
    inClinicLocationNumber: '',
    colorCode: 'blue',
    active: true
  });

  useEffect(() => {
    dispatch(fetchDevices());
  }, [dispatch]);

  useEffect(() => {
    if (devices && id) {
      const device = devices.find(d => d.id === id);
      if (device) {
        setFormData({
          name: device.name || '',
          serialNumber: device.serialNumber || '',
          inClinicLocation: device.inClinicLocation || '',
          inClinicLocationNumber: device.inClinicLocationNumber?.toString() || '',
          colorCode: (device.colorCode as PadColorKeys) || 'blue',
          active: device.active
        });
      }
    }
  }, [devices, id]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!id) return;

    try {
      const device = devices.find(d => d.id === id);
      if (!device) {
        throw new Error('Device not found');
      }

      const updatedDevice: Device = {
        ...device,
        name: formData.name,
        serialNumber: device.serialNumber, // Keep original serial number
        inClinicLocation: formData.inClinicLocation,
        inClinicLocationNumber: parseInt(formData.inClinicLocationNumber) || undefined,
        colorCode: formData.colorCode,
        active: formData.active
      };

      await dispatch(updateDevice(updatedDevice));
      navigate('/devices');
    } catch (err) {
      console.error('Failed to update device:', err);
    }
  };

  const handleDelete = async () => {
    if (!id) return;
    try {
      await dispatch(deleteDevice(id));
      navigate('/devices');
    } catch (err) {
      console.error('Failed to remove device:', err);
    }
  };

  const handleChange = (field: keyof DeviceFormData, value: string | boolean) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  if (loading) {
    return (
      <div className="text-center py-5">
        <Spinner animation="border" role="status">
          <span className="visually-hidden">Loading...</span>
        </Spinner>
      </div>
    );
  }

  if (error) {
    return (
      <Alert variant="danger">
        Error loading device: {error}
      </Alert>
    );
  }

  const colorOptions: { value: PadColorKeys; label: string }[] = [
    { value: 'red', label: 'Red' },
    { value: 'pink', label: 'Pink' },
    { value: 'orange', label: 'Orange' },
    { value: 'yellow', label: 'Yellow' },
    { value: 'green', label: 'Green' },
    { value: 'blue', label: 'Blue' },
    { value: 'purple', label: 'Purple' },
  ];

  return (
    <Container className="py-5">
      <Card className="mx-auto" style={{ maxWidth: '600px' }}>
        <Card.Body>
          <h3 className="text-center mb-4">Edit Device</h3>
          
          <Form onSubmit={handleSubmit}>
            <Form.Group className="mb-3">
              <Form.Label>Device Name</Form.Label>
              <Form.Control
                type="text"
                value={formData.name}
                onChange={(e) => handleChange('name', e.target.value)}
                placeholder="Enter device name"
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Serial Number</Form.Label>
              <Form.Control
                type="text"
                value={formData.serialNumber}
                readOnly
                disabled
                className="bg-light"
              />
              <Form.Text className="text-muted">
                Serial number cannot be changed as it is a permanent identifier.
              </Form.Text>
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Location</Form.Label>
              <Form.Control
                type="text"
                value={formData.inClinicLocation}
                onChange={(e) => handleChange('inClinicLocation', e.target.value)}
                placeholder="Enter location"
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Location Number</Form.Label>
              <Form.Control
                type="text"
                value={formData.inClinicLocationNumber}
                onChange={(e) => handleChange('inClinicLocationNumber', e.target.value)}
                placeholder="Enter location number"
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Color</Form.Label>
              <div className="d-flex align-items-center gap-3">
                <Form.Select
                  value={formData.colorCode}
                  onChange={(e) => handleChange('colorCode', e.target.value as PadColorKeys)}
                  style={{ flex: 1 }}
                >
                  {colorOptions.map(({ value, label }) => (
                    <option key={value} value={value}>
                      {label}
                    </option>
                  ))}
                </Form.Select>
                <div 
                  style={{ 
                    width: '40px', 
                    height: '40px', 
                    backgroundColor: formData.colorCode,
                    border: '1px solid #dee2e6',
                    borderRadius: '4px'
                  }} 
                />
              </div>
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Check
                type="switch"
                id="active-switch"
                label="Active"
                checked={formData.active}
                onChange={(e) => handleChange('active', e.target.checked)}
              />
            </Form.Group>

            <div className="d-grid gap-2">
              <Button variant="primary" type="submit">
                Save Changes
              </Button>
              <Button variant="secondary" onClick={() => navigate('/devices')}>
                Cancel
              </Button>
              <Button 
                variant="danger" 
                onClick={() => setShowDeleteConfirm(true)}
                className="mt-3"
              >
                Remove from Organization
              </Button>
            </div>
          </Form>
        </Card.Body>
      </Card>

      <Modal show={showDeleteConfirm} onHide={() => setShowDeleteConfirm(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Confirm Removal</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          Are you sure you want to remove this device from your organization? This action cannot be undone.
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowDeleteConfirm(false)}>
            Cancel
          </Button>
          <Button variant="danger" onClick={handleDelete}>
            Remove Device
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
};

export default EditDevicePage; 